import { replace } from 'connected-react-router';
import { put, call } from 'redux-saga/effects';

import {
  notifyAPIError,
  notifyAPIAuthenticationError,
  reportUnrecoverableError,
  reportRecoverableError,
} from '../../actions';
import { INTERNAL_ERROR } from '../../constants/errors';
import { RECOVERABLE_ERROR, UNRECOVERABLE_ERROR } from '../../constants/heapEvents';
import { track } from '../../lib/heapHelpers';
import { notifyHoneybadger } from '../../lib/notifyHoneybadger';

const isCriticalError = error => {
  const status = errorStatus(error);
  return status >= 500;
};

const isPreconditionFailedError = error => {
  const status = errorStatus(error);
  return status === 412;
};

const isRecoverableError = error => {
  const status = errorStatus(error);
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 404;
};

const isAuthenticationError = error => {
  const status = errorStatus(error);
  return status === 401;
};

const isResourceNotFoundError = error => {
  const status = errorStatus(error);
  return status === 404;
};

const errorStatus = error => {
  const { response = {} } = error;
  const { status } = response;
  return status;
};

const extractErrorDetails = error => {
  const { response } = error;
  const { status, data, statusText } = response;
  return { status, statusText, data };
};

const isInternalValidationError = error => {
  const { status } = error;
  return status === INTERNAL_ERROR;
};

const formatErrorTracking = e => {
  const { status, data, statusText } = e.response;
  const { error, traceId, errorMessage } = data;
  return {
    'Application Error Trace ID': traceId,
    'Application Error Type': error,
    'Application Error Message': errorMessage.join('|'),
    'Application Error Status': status,
    'Application Error Status Message': statusText,
  };
};

const reportErrorToHoneybadger = e => {
  const config = e.config || {};
  const errorMessage =
    'API Error' +
    (config.url ? `: ${e.response?.status || ''} during ${config.method} ${config.url}` : '');

  const context = {
    url: config.url,
    params: config.data,
    method: config.method,
    request: e.request,
    response: e.response,
    ...(e.action || {}),
  };
  notifyHoneybadger(errorMessage, context);
};

function* manageAPIError(error) {
  reportErrorToHoneybadger(error);

  if (isCriticalError(error)) {
    yield call(track, UNRECOVERABLE_ERROR, formatErrorTracking(error));
    yield put(reportUnrecoverableError(error));
    yield put(replace('/unrecoverable-error'));
  } else if (isPreconditionFailedError(error)) {
    yield put(replace('/course-under-review'));
  } else if (isAuthenticationError(error)) {
    yield put(notifyAPIAuthenticationError());
  } else if (isRecoverableError(error)) {
    yield call(track, RECOVERABLE_ERROR, formatErrorTracking(error));
    yield put(reportRecoverableError(extractErrorDetails(error)));
    yield put(replace('/error'));
  } else if (isResourceNotFoundError(error)) {
    yield put(replace('/resource-not-found'));
  } else if (error.useFallbackError !== false) {
    yield put(notifyAPIError(error));
  }
}

export {
  isCriticalError,
  isAuthenticationError,
  isRecoverableError,
  manageAPIError,
  errorStatus,
  isInternalValidationError,
  isResourceNotFoundError,
};
